import { PLAN_STATE, type PlanState, UserRoles } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { UserRole } from "@next-platform/packages-ts-types";
import React, { type ReactNode } from "react";

import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { getPlanDisplayColors } from "@/constants/planTierColors";
import { cn } from "@/utils";

type DetailsRow = {
  rowId: string;
  label: ReactNode;
  value: ReactNode;
  valueIcon?: IconVariants;
  valueIconClassName?: string;
  valueClassName?: string;
};

type GetDetailsContentParams = {
  planState: PlanState;
  planName: string;
  previousPlanName: string;
  isFreePlanRole: boolean;
};

const getDetailsContent = ({ planState, planName, previousPlanName, isFreePlanRole }: GetDetailsContentParams): DetailsRow[] => {
  if (planState === PLAN_STATE.EXPIRED) {
    return [
      {
        rowId: "previous-plan",
        label: <Trans i18nKey="subscription:overview.planDetails.previousPlan" />,
        value: previousPlanName,
      },
      {
        rowId: "payment-type",
        label: <Trans i18nKey="subscription:overview.planDetails.paymentType" />,
        value: <Trans i18nKey="subscription:overview.paymentType.oneTimePayment" />,
        valueIcon: IconVariants.CLOCK,
      },
      {
        rowId: "status",
        label: <Trans i18nKey="subscription:overview.planDetails.status" />,
        value: <Trans i18nKey="user:profile.currentPlan.statusLabel.expired" />,
        valueIcon: IconVariants.CLOSE_CIRCLE,
        valueIconClassName: "text-red-10 shrink-0",
        valueClassName: "text-red-10",
      },
      {
        rowId: "current-plan",
        label: <Trans i18nKey="subscription:overview.planDetails.currentPlan" />,
        value: isFreePlanRole ? <Trans i18nKey="subscription:overview.planDetails.currentPlanFree" /> : planName,
        valueClassName: "font-semibold",
      },
    ];
  }

  return [
    {
      rowId: "plan-name",
      label: <Trans i18nKey="subscription:overview.planDetails.planName" />,
      value: planName,
    },
    {
      rowId: "payment-type",
      label: <Trans i18nKey="subscription:overview.planDetails.paymentType" />,
      value: <Trans i18nKey="subscription:overview.paymentType.oneTimePayment" />,
      valueIcon: IconVariants.CLOCK,
    },
    {
      rowId: "renews",
      label: <Trans i18nKey="subscription:overview.planDetails.renews" />,
      value: <Trans i18nKey="subscription:overview.planDetails.renewsNo" />,
    },
  ];
};

type SubscriptionPlanDetailsProps = {
  planRole: UserRole;
  tierRole: UserRole;
  planState: PlanState;
  planName: string;
  previousPlanName: string;
};

const SubscriptionPlanDetails = ({ planRole, tierRole, planState, planName, previousPlanName }: SubscriptionPlanDetailsProps) => {
  const colors = getPlanDisplayColors({ role: tierRole, planState });
  const tierColors = getPlanDisplayColors({ role: tierRole, planState: PLAN_STATE.ONE_TIME_ACTIVE });
  const isFreePlanRole = planRole === UserRoles.FREE;
  const detailRows = getDetailsContent({ planState, planName, previousPlanName, isFreePlanRole });
  const paymentTypeIconClassName = cn(tierColors.text, "shrink-0");

  const resolveValueIconClassName = (row: DetailsRow): string | undefined => {
    if (!row.valueIcon) {
      return undefined;
    }

    if (row.valueIconClassName) {
      return row.valueIconClassName;
    }

    if (row.rowId === "payment-type") {
      return paymentTypeIconClassName;
    }

    return cn(colors.text, "shrink-0");
  };

  return (
    <Col className="gap-sm" data-testid="SubscriptionPlan-Details">
      <Text variant={TextVariants.LABEL_SMALL_MEDIUM} className="uppercase text-gray-11 tracking-wider">
        <Trans i18nKey="subscription:overview.planDetails.title" />
      </Text>
      {detailRows.map((row, index) => {
        const { rowId, label, value, valueIcon, valueClassName } = row;

        return (
          <React.Fragment key={rowId}>
            <Row align="center" justify="between" className="gap-sm py-sm" data-testid="SubscriptionPlan-DetailRow">
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-11">
                {label}
              </Text>
              <Row align="center" className="gap-sm min-w-0 justify-end">
                {valueIcon ? <Icon icon={valueIcon} size={IconSize.SMALL} className={resolveValueIconClassName(row)} /> : null}
                <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className={cn("text-gray-12 text-right", valueClassName)}>
                  {value}
                </Text>
              </Row>
            </Row>
            {index < detailRows.length - 1 ? <hr className="border border-solid border-gray-4" /> : null}
          </React.Fragment>
        );
      })}
    </Col>
  );
};

export default SubscriptionPlanDetails;
